// ============================================================================
// CASE TYPE MASTER SCREEN — MSTR-CASETYPE-001 — Phase 8a
// ============================================================================
// Registry of litigation case types (ARBITRATION, NI_ACT_138, CIVIL, SARFAESI,
// EXECUTION). Feeds Phase 9 Litigation Case Shell — each row binds case type
// to EWF workflow definition + frontend detail-panel registry key.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ConfirmDialog, ModuleHeader } from 'components/modals';
import type { ActionButton } from 'components/modals';
import {
  type ActionMenuItem,
  SyutGrid,
  type SyutGridColumn,
  SyutSelect,
  MinimizedModalStrip,
} from 'components/ui';
import notificationService from 'services/notificationService';
import { useModalMinimize } from 'contexts/ModalMinimizeContext';
import { useCaseTypes } from 'hooks/api/useCaseTypes';
import { caseTypeService } from 'api/services/caseTypeService';
import type { CaseType, CaseTypeSearchRequest } from 'api/types/caseType.types';
import CaseTypeFormModal from 'components/modals/modules/case-type-module/CaseTypeFormModal';

type ViewMode = 'list' | 'grid';

interface CaseTypeMasterScreenProps {
  tabId?: string;
  moduleName?: string;
}

const CaseTypeMasterScreen: React.FC<CaseTypeMasterScreenProps> = ({ tabId, moduleName }) => {
  const { isMinimized, dismiss: dismissMinimized, getMinimizedModal } = useModalMinimize();

  const [queryParams, setQueryParams] = useState<CaseTypeSearchRequest>({
    page: 1,
    pageSize: 25,
  });
  const { data, loading, pagination, refetch } = useCaseTypes(queryParams);

  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [searchQuery, setSearchQuery] = useState('');
  const [filterOpen, setFilterOpen] = useState(false);

  const [caseTypeCodeFilter, setCaseTypeCodeFilter] = useState('');
  const [displayNameFilter, setDisplayNameFilter] = useState('');
  const [securedFilter, setSecuredFilter] = useState<'' | 'true' | 'false'>('');
  const [statusFilter, setStatusFilter] = useState<string>('');

  useEffect(() => {
    const t = setTimeout(() => {
      setQueryParams(prev => ({
        ...prev,
        searchTerm: searchQuery.trim() || undefined,
        page: 1,
      }));
    }, 400);
    return () => clearTimeout(t);
  }, [searchQuery]);

  const [formModalOpen, setFormModalOpen] = useState(false);
  const [formMode, setFormMode] = useState<'create' | 'edit'>('create');
  const [selectedCaseType, setSelectedCaseType] = useState<CaseType | null>(null);

  const createInstanceCounter = useRef(0);
  const [createInstanceId, setCreateInstanceId] = useState<string>('0');
  const formModalMinimizeRef = useRef<(() => void) | null>(null);

  const handleRestoreModal = useCallback(
    (restoredMode: 'create' | 'edit', instanceId?: string, recordCode?: string) => {
      if (instanceId) setCreateInstanceId(instanceId);
      if (recordCode) {
        const rec = data.find(c => c.caseTypeCode === recordCode);
        if (rec) setSelectedCaseType(rec);
      }
      setFormMode(restoredMode);
      setFormModalOpen(true);
    },
    [data]
  );

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [caseTypeToDelete, setCaseTypeToDelete] = useState<CaseType | null>(null);

  const handleAddNew = useCallback(() => {
    if (formModalOpen) {
      formModalMinimizeRef.current?.();
    }
    const newId = String(++createInstanceCounter.current);
    setCreateInstanceId(newId);
    setSelectedCaseType(null);
    setFormMode('create');
    setFormModalOpen(true);
  }, [formModalOpen]);

  const handleEdit = useCallback(
    (caseType: CaseType) => {
      const modalId = tabId ? `${tabId}-form-modal-edit-${caseType.caseTypeCode}` : null;
      if (modalId && isMinimized(modalId)) {
        const modal = getMinimizedModal(modalId);
        dismissMinimized(modalId);
        modal?.onRestore?.();
        return;
      }
      setSelectedCaseType(caseType);
      setFormMode('edit');
      setFormModalOpen(true);
    },
    [tabId, isMinimized, dismissMinimized, getMinimizedModal]
  );

  const handleDeleteClick = useCallback((caseType: CaseType) => {
    setCaseTypeToDelete(caseType);
    setDeleteModalOpen(true);
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    if (!caseTypeToDelete) return;
    setIsDeleting(true);
    try {
      await caseTypeService.delete(caseTypeToDelete.id);
      setIsDeleting(false);
      setDeleteModalOpen(false);
      notificationService.toast.success(`Case type "${caseTypeToDelete.caseTypeCode}" deleted successfully.`);
      setCaseTypeToDelete(null);
      refetch();
    } catch (err: any) {
      setIsDeleting(false);
      const msg = err?.response?.data?.message || 'An error occurred while deleting.';
      notificationService.toast.error(msg, 6000);
    }
  }, [caseTypeToDelete, refetch]);

  const handleSaved = useCallback(() => { refetch(); }, [refetch]);
  const handleRefresh = useCallback(() => { refetch(); }, [refetch]);

  const handlePageChange = useCallback((pageIndex: number) => {
    setQueryParams(prev => ({ ...prev, page: pageIndex + 1 }));
  }, []);
  const handlePageSizeChange = useCallback((pageSize: number) => {
    setQueryParams(prev => ({ ...prev, pageSize, page: 1 }));
  }, []);

  const handleApplyFilters = useCallback(() => {
    setQueryParams(prev => ({
      ...prev,
      caseTypeCode: caseTypeCodeFilter.trim() || undefined,
      displayName: displayNameFilter.trim() || undefined,
      isSecuredOnly: securedFilter === '' ? undefined : securedFilter === 'true',
      statusCode: statusFilter || undefined,
      page: 1,
    }));
    setFilterOpen(false);
  }, [caseTypeCodeFilter, displayNameFilter, securedFilter, statusFilter]);

  const handleClearFilters = useCallback(() => {
    setCaseTypeCodeFilter('');
    setDisplayNameFilter('');
    setSecuredFilter('');
    setStatusFilter('');
    setQueryParams({ page: 1, pageSize: queryParams.pageSize ?? 25 });
  }, [queryParams.pageSize]);

  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (queryParams.caseTypeCode) count++;
    if (queryParams.displayName) count++;
    if (queryParams.isSecuredOnly !== undefined) count++;
    if (queryParams.statusCode) count++;
    return count;
  }, [queryParams.caseTypeCode, queryParams.displayName, queryParams.isSecuredOnly, queryParams.statusCode]);

  const columns: SyutGridColumn<CaseType>[] = useMemo(() => [
    {
      id: 'caseTypeCode',
      header: 'Case Type Code',
      accessorKey: 'caseTypeCode',
      size: 160,
      enableSorting: true,
    },
    {
      id: 'displayName',
      header: 'Display Name',
      accessorKey: 'displayName',
      size: 240,
      enableSorting: true,
    },
    {
      id: 'workflowDefinitionCode',
      header: 'Workflow Definition',
      accessorKey: 'workflowDefinitionCode',
      size: 200,
      enableSorting: true,
      cell: ({ row }) => row.original.workflowDefinitionCode ?? <span style={{ color: 'var(--text-secondary)' }}>—</span>,
    },
    {
      id: 'isSecuredOnly',
      header: 'Secured Only',
      accessorKey: 'isSecuredOnly',
      size: 110,
      enableSorting: true,
      cell: ({ row }) => row.original.isSecuredOnly
        ? <span style={{ fontSize: '0.7rem', fontWeight: 600, color: '#1e40af', padding: '2px 8px', border: '1px solid #1e40af', borderRadius: '999px' }}>Secured</span>
        : <span style={{ color: 'var(--text-secondary)', fontSize: '0.8rem' }}>—</span>,
    },
    {
      id: 'displayOrder',
      header: 'Order',
      accessorKey: 'displayOrder',
      size: 80,
      enableSorting: true,
    },
    {
      id: 'statusCode',
      header: 'Status',
      accessorKey: 'statusCode',
      size: 110,
      enableSorting: true,
      cell: ({ row }) => {
        const c = row.original;
        const code = c.deletedAt ? 'DELETED' : (c.statusCode || 'ACTIVE');
        let color = '#6b7280';
        if (code === 'ACTIVE') color = '#16a34a';
        else if (code === 'DELETED') color = '#dc2626';
        return (
          <span
            style={{
              fontSize: '0.75rem',
              fontWeight: 600,
              color,
              padding: '2px 8px',
              border: `1px solid ${color}`,
              borderRadius: '999px',
            }}
            title={c.deletedAt ? `Deleted at ${new Date(c.deletedAt).toLocaleString()}` : undefined}
          >
            {code}
          </span>
        );
      },
    },
    {
      id: 'updatedAt',
      header: 'Last Updated',
      accessorKey: 'updatedAt',
      size: 150,
      enableSorting: true,
      cell: ({ row }) => {
        const v = row.original.updatedAt;
        if (!v) return '-';
        try { return new Date(v).toLocaleString(); } catch { return v; }
      },
    },
  ], []);

  const getRowActions = useCallback((caseType: CaseType): ActionMenuItem[] => [
    {
      id: 'edit',
      label: 'Edit',
      icon: <IconSystem.edit size={16} />,
      onClick: () => handleEdit(caseType),
    },
    {
      id: 'delete',
      label: 'Delete',
      icon: <IconSystem.trash size={16} />,
      onClick: () => handleDeleteClick(caseType),
      variant: 'danger',
    },
  ], [handleEdit, handleDeleteClick]);

  const actionButtons: ActionButton[] = useMemo(() => [
    {
      id: 'add',
      icon: <IconSystem.add size={18} />,
      label: 'Add',
      tooltip: 'Add New Case Type',
      onClick: handleAddNew,
      variant: 'icon',
    },
    {
      id: 'refresh',
      icon: <IconSystem.refresh size={18} />,
      label: 'Refresh',
      tooltip: 'Refresh',
      onClick: handleRefresh,
      variant: 'icon',
    },
  ], [handleAddNew, handleRefresh]);

  return (
    <div className="module-master-container">
      <ModuleHeader
        title="Case Type Master"
        description="Registry of litigation case types. Each row binds a case type to its EWF workflow definition and to its frontend detail-panel."
        icon={<IconSystem.tag size={18} />}
        searchConfig={{
          value: searchQuery,
          onChange: setSearchQuery,
          placeholder: 'Search code, name, description...',
        }}
        viewModeConfig={{
          currentMode: viewMode,
          modes: [
            { id: 'list', icon: <IconSystem.list size={16} />, label: 'List', tooltip: 'List view' },
            { id: 'grid', icon: <IconSystem.grid size={16} />, label: 'Grid', tooltip: 'Grid view' },
          ],
          onChange: (mode) => setViewMode(mode as ViewMode),
        }}
        filterConfig={{
          onClick: () => setFilterOpen(!filterOpen),
          isActive: filterOpen || activeFilterCount > 0,
          activeFilterCount,
        }}
        leadingToolbarItems={tabId ? <MinimizedModalStrip tabId={tabId} /> : undefined}
        actions={actionButtons}
      />

      <div className="module-content" style={{ marginTop: '6px' }}>
        <div style={{ display: 'flex', gap: '0', flex: 1, overflow: 'hidden' }}>
          <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
            {viewMode === 'list' && (
              <SyutGrid
                data={data}
                columns={columns}
                enableSorting
                enablePagination
                manualPagination
                totalCount={pagination.totalItems}
                currentPage={(queryParams.page ?? 1) - 1}
                pageSize={queryParams.pageSize ?? 25}
                onPageChange={handlePageChange}
                onPageSizeChange={handlePageSizeChange}
                rowActions={getRowActions}
                isLoading={loading}
                emptyMessage="No case types found"
              />
            )}

            {viewMode === 'grid' && (
              <div
                className="syut-grid-component"
                style={{ height: '100%', display: 'flex', flexDirection: 'column' }}
              >
                <div
                  style={{
                    padding: '1rem',
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
                    gap: '1rem',
                    flex: 1,
                    overflow: 'auto',
                    alignContent: 'start',
                  }}
                >
                  {data.map((c) => (
                    <div
                      key={String(c.id)}
                      style={{
                        border: '1px solid var(--border-color)',
                        borderRadius: '8px',
                        padding: '1rem',
                        background: 'var(--bg-primary)',
                        cursor: 'pointer',
                      }}
                      onClick={() => handleEdit(c)}
                    >
                      <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', fontWeight: 600 }}>
                        {c.caseTypeCode}
                      </div>
                      <div style={{ fontSize: '1rem', fontWeight: 600, marginTop: '0.25rem', color: 'var(--text-primary)' }}>
                        {c.displayName}
                      </div>
                      {c.workflowDefinitionCode && (
                        <div style={{ fontSize: '0.7rem', marginTop: '0.5rem', color: 'var(--text-secondary)' }}>
                          Workflow: <code>{c.workflowDefinitionCode}</code>
                        </div>
                      )}
                      <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem', flexWrap: 'wrap' }}>
                        {c.isSecuredOnly && (
                          <span style={{ fontSize: '0.65rem', fontWeight: 600, color: '#1e40af', padding: '1px 6px', border: '1px solid #1e40af', borderRadius: '999px' }}>
                            Secured
                          </span>
                        )}
                        <span style={{ fontSize: '0.7rem', color: c.statusCode === 'ACTIVE' ? '#16a34a' : '#6b7280' }}>
                          {c.statusCode || 'ACTIVE'}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {filterOpen && (
        <div className="search-panel">
          <div className="search-panel-header">
            <h3 className="search-panel-title">Filters</h3>
            <button
              className="search-panel-close"
              onClick={() => setFilterOpen(false)}
              title="Close filters"
            >
              <IconSystem.x size={14} />
            </button>
          </div>
          <div className="search-panel-content">
            <div className="filter-section">
              <label className="form-label" style={{ fontSize: '0.8125rem', marginBottom: '0.25rem' }}>
                Case Type Code
              </label>
              <input
                type="text"
                className="form-input"
                value={caseTypeCodeFilter}
                onChange={(e) => setCaseTypeCodeFilter(e.target.value)}
                placeholder="Filter by code..."
                style={{ fontSize: '0.8125rem', padding: '0.375rem 0.625rem' }}
              />
            </div>

            <div className="filter-section">
              <label className="form-label" style={{ fontSize: '0.8125rem', marginBottom: '0.25rem' }}>
                Display Name
              </label>
              <input
                type="text"
                className="form-input"
                value={displayNameFilter}
                onChange={(e) => setDisplayNameFilter(e.target.value)}
                placeholder="Filter by name..."
                style={{ fontSize: '0.8125rem', padding: '0.375rem 0.625rem' }}
              />
            </div>

            <div className="filter-section">
              <SyutSelect
                label="Secured Only"
                options={[
                  { value: '',      label: 'Any' },
                  { value: 'true',  label: 'Yes (secured)' },
                  { value: 'false', label: 'No' },
                ]}
                value={securedFilter}
                onChange={(v) => setSecuredFilter(((v as string) || '') as '' | 'true' | 'false')}
                isClearable={false}
                size="sm"
              />
            </div>

            <div className="filter-section">
              <SyutSelect
                label="Status"
                options={[
                  { value: '',         label: 'Active only (default)' },
                  { value: 'INACTIVE', label: 'Inactive only' },
                  { value: 'DELETED',  label: 'Deleted only' },
                  { value: 'ALL',      label: 'All (incl. deleted)' },
                ]}
                value={statusFilter}
                onChange={(v) => setStatusFilter((v as string) || '')}
                isClearable={false}
                size="sm"
              />
            </div>
          </div>
          <div className="filter-actions">
            <button className="btn btn-secondary" onClick={handleClearFilters}>
              Clear
            </button>
            <button className="btn btn-primary" onClick={handleApplyFilters}>
              Apply
            </button>
          </div>
        </div>
        )}
      </div>

      <ConfirmDialog
        open={deleteModalOpen}
        title="Confirm Delete"
        message={
          caseTypeToDelete
            ? `Are you sure you want to delete case type "${caseTypeToDelete.caseTypeCode}"? This action cannot be undone.`
            : ''
        }
        confirmLabel="Delete"
        cancelLabel="Cancel"
        variant="danger"
        loading={isDeleting}
        loadingMessage="Deleting..."
        onConfirm={handleConfirmDelete}
        onCancel={() => {
          if (!isDeleting) {
            setDeleteModalOpen(false);
            setCaseTypeToDelete(null);
          }
        }}
      />

      <CaseTypeFormModal
        isOpen={formModalOpen}
        mode={formMode}
        record={selectedCaseType}
        onSaved={handleSaved}
        onClose={() => setFormModalOpen(false)}
        tabId={tabId}
        moduleName={moduleName}
        onRestoreModal={handleRestoreModal}
        createInstanceId={createInstanceId}
        minimizeRef={formModalMinimizeRef}
      />
    </div>
  );
};

export default CaseTypeMasterScreen;
